import { SignUp } from '@clerk/clerk-react'
import { Ambient } from '@/components/ui/Ambient'
import { FEATURE_ORGANISATIONS } from '@/lib/featureFlags'
import { JoinOrgTabs } from '@/components/shell/JoinOrgPanel'
import { AuthFormFrame } from '@/components/ui/AuthFormFrame'
import { useLocation, useSearchParams } from 'react-router-dom'
import { usePageTitle } from '@/lib/pageTitle'
import { REDIRECT_PARAM, authUrlWithRedirect, locationToPath, safeRedirect } from '@/lib/redirect'
import { useIsPhone } from '@/lib/useIsPhone'
import { authAppearance } from '@/lib/clerkAppearance'
import { EmailCodeStep } from '@/components/ui/EmailCodeStep'

export function SignUpPage() {
  usePageTitle('Create account')
  // Mirror SignInPage: honor `?redirect_url=` so a deep link (e.g. a shared
  // /library/:id?share=:courseId) returns the user to the intended page after
  // signing up, and hand it back to /sign-in for the "Sign in instead" hop.
  const [params] = useSearchParams()
  const location = useLocation()
  const legacy = (location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null)?.from
  // An organisation invitation link passes the invited email, so it is already filled in.
  const email = params.get('email') ?? undefined
  const from = safeRedirect(params.get(REDIRECT_PARAM) ?? (legacy ? locationToPath(legacy) : null))
  // Phones (16 Oct 2026, design “Create your account”, 375px): a white page — the logo, then the
  // form at full width with no card, Clerk's “Already have an account?” band at the bottom.
  const phone = useIsPhone()
  // Phone only: on desktop the form stays Clerk's own, matching the sign-in page (code review, 16 Oct).
  const appearance = phone ? authAppearance(true) : undefined
  const signUp = (
    <AuthFormFrame>
    <SignUp
      initialValues={email ? { emailAddress: email } : undefined}
      routing="path"
      path="/sign-up"
      signInUrl={authUrlWithRedirect('/sign-in', from)}
      forceRedirectUrl={from ?? '/'}
      appearance={appearance}
    />
    </AuthFormFrame>
  )

  // The email code step on a phone is our own (23 Oct 2026, design "Check your email"); every other
  // step is Clerk's form.
  if (phone && location.pathname.startsWith('/sign-up/verify-email-address')) return <EmailCodeStep redirectTo={from ?? '/'} />

  if (phone) return (
    <div style={{ minHeight: '100dvh', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', overflowX: 'hidden' }}>
      <img src="/learnbee-logo.png" alt="Learnbee" style={{ height: 34, width: 'auto', margin: '28px auto 14px', flex: 'none' }} />
      {FEATURE_ORGANISATIONS
        ? <JoinOrgTabs compact formLabel="Create account" redirectTo={from ?? '/'} formOnly={!!from?.startsWith('/join/org/')} form={signUp} />
        : signUp}
    </div>
  )

  // Same ground as the journey — the flat grey made creating an account feel
  // like leaving the product to fill in a form somewhere else.
  return (
    <>
    <Ambient />
    {/* Pinned to the top, not centred (21 Oct 2026): each Clerk step is a different height, so a
        centred page moved the logo, tabs and card up and down on every Continue. */}
    <div className="min-h-screen flex flex-col items-center justify-start px-4 pb-4 relative" style={{ zIndex: 1, paddingTop: 'clamp(32px, 10vh, 96px)' }}>
      <div className="mb-8 text-center">
        {/* learnbee-logo.png is the transparent wordmark; /learnbee.png
            carries a white plate that shows as a box on a tinted ground. */}
        <img src="/learnbee-logo.png" alt="Learnbee" className="h-9 w-auto mx-auto mb-2" />
        <p className="text-gray-500 text-sm">AI-powered course creation</p>
      </div>
      {/* A deep link wins: someone who signed up to claim a draft from the
          website lands on /resume, and someone opening a shared course lands
          on that course. A signup with no destination goes to their courses. */}
      {/* Organisations (dev only): a second way in — join an organisation with a code. */}
      {FEATURE_ORGANISATIONS ? (
        <JoinOrgTabs formLabel="Create account" redirectTo={from ?? '/'} formOnly={!!from?.startsWith('/join/org/')} form={signUp} />
      ) : (
        signUp
      )}
    </div>
    </>
  )
}
